'use client';

import { useEffect, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { LoginRootWrapper } from './login/components/LoginRootWrapper';
import { isLoginPathname } from '@/lib/utils/loginPath';

type ShellComponent = ComponentType<{ children: ReactNode }>;
type LoadShopShell = () => Promise<{ ShopClientWrapper: ShellComponent }>;

const defaultLoadShopShell: LoadShopShell = () => import('./ShopClientWrapper.client');

/**
 * Login-first document shell.
 *
 * Soft navigations reuse the root layout client tree (Router Cache), so after
 * login → /shop we must switch to the shop shell here. ShopClientWrapper is
 * imported only when leaving /login so the shared-ui mega-chunk stays off the
 * login first paint.
 */
export function LoginDocumentShell({
    children,
    loadShopShell = defaultLoadShopShell,
}: {
    children: ReactNode;
    /** Test seam — production always uses the default dynamic import. */
    loadShopShell?: LoadShopShell;
}): React.ReactElement | null {
    const pathname = usePathname();
    const lastPathRef = useRef<string | null>(pathname);
    if (pathname != null) {
        lastPathRef.current = pathname;
    }
    const effectivePath = pathname ?? lastPathRef.current;
    const leftLogin = Boolean(effectivePath && !isLoginPathname(effectivePath));
    const [ShopShell, setShopShell] = useState<ShellComponent | null>(null);

    useEffect(() => {
        if (!leftLogin || ShopShell) {
            return undefined;
        }

        let cancelled = false;
        void loadShopShell()
            .then((module) => {
                if (!cancelled) {
                    setShopShell(() => module.ShopClientWrapper);
                }
            })
            .catch(() => {
                // Chunk load failure would otherwise leave a permanent black
                // screen (ShopShell stays null). Hard-navigate so the server
                // serves ShopDocumentShell with providers.
                if (!cancelled && typeof window !== 'undefined') {
                    window.location.assign(
                        `${window.location.pathname}${window.location.search}${window.location.hash}`,
                    );
                }
            });

        return () => {
            cancelled = true;
        };
    }, [leftLogin, ShopShell, loadShopShell]);

    if (leftLogin) {
        if (!ShopShell) {
            // Do not mount shop routes without QueryClient/LoadingProvider.
            // The root layout's StoreLoadingCover is already covering the
            // screen, so rendering nothing here shows one loader, not two.
            return null;
        }
        return <ShopShell>{children}</ShopShell>;
    }

    return <LoginRootWrapper>{children}</LoginRootWrapper>;
}
